'use client'

import {
  useEffect,
  useMemo,
  useState,
} from 'react'

import {
  Check,
  CreditCard,
  Loader2,
  Plus,
  Search,
} from 'lucide-react'

import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'

import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert'

import { cn } from '@/lib/utils'

import { apiGetSubscriptions } from '@/lib/api/subscriptions'

import type { SubscriptionItem } from '@/types/subscription'

import { CreateSubscriptionModal } from './CreateSubscriptionModal'

function formatMoney(
  value: number
) {
  return new Intl.NumberFormat(
    'es-MX',
    {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 2,
    }
  ).format(
    Number(value || 0)
  )
}

function frequencyLabel(
  value: string
) {
  switch (value) {
    case 'day':
      return 'día'

    case 'week':
      return 'semana'

    case 'semi_month':
      return 'quincena'

    case 'month':
      return 'mes'

    case 'year':
      return 'año'

    default:
      return value
  }
}

function normalizeIds(
  ids: number[]
) {
  return Array.from(
    new Set(
      (Array.isArray(ids) ? ids : [])
        .map((id) => Number(id))
        .filter(
          (id) =>
            Number.isFinite(id) &&
            id > 0
        )
    )
  )
}

export function CommunitySubscriptionsSelector({
  value,
  onChange,
}: {
  value: number[]
  onChange: (
    ids: number[]
  ) => void
}) {
  const selectedIds =
    normalizeIds(value)

  const [loading, setLoading] =
    useState(true)

  const [error, setError] =
    useState<string | null>(null)

  const [query, setQuery] =
    useState('')

  const [items, setItems] =
    useState<
      SubscriptionItem[]
    >([])

  const [
    openCreate,
    setOpenCreate,
  ] = useState(false)

  async function load(
    search = ''
  ) {
    try {
      setLoading(true)
      setError(null)

      const data =
        await apiGetSubscriptions(
          search
        )

      setItems(
        Array.isArray(data)
          ? data
          : []
      )
    } catch (e: any) {
      setError(
        e?.message ||
          'No se pudieron cargar las suscripciones'
      )
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const filtered =
    useMemo(() => {
      const q =
        query
          .trim()
          .toLowerCase()

      if (!q) {
        return items
      }

      return items.filter(
        (item) =>
          String(
            item.name || ''
          )
            .toLowerCase()
            .includes(q)
      )
    }, [
      items,
      query,
    ])

  function toggleSubscription(
    recurringId: number
  ) {
    const id =
      Number(recurringId)

    if (!id) {
      return
    }

    const exists =
      selectedIds.includes(id)

    if (exists) {
      onChange(
        selectedIds.filter(
          (currentId) =>
            currentId !== id
        )
      )

      return
    }

    onChange([
      ...selectedIds,
      id,
    ])
  }

  const selectedItems =
    useMemo(() => {
      const idSet =
        new Set(selectedIds)

      return items.filter(
        (item) =>
          idSet.has(
            Number(
              item.recurring_id
            )
          )
      )
    }, [
      items,
      selectedIds,
    ])

  return (
    <>
      <Card className="space-y-5 p-6">
        <div className="flex flex-col items-start justify-between gap-4 lg:flex-row">
          <div>
            <h2 className="text-base font-semibold">
              Suscripciones
            </h2>

            <p className="text-sm text-muted-foreground">
              Selecciona uno o varios
              planes que permitan entrar a
              la comunidad. Esta opción
              puede convivir con pago único
              y acceso por producto.
            </p>
          </div>

          <Button
            type="button"
            onClick={() =>
              setOpenCreate(true)
            }
            className="gap-2"
          >
            <Plus className="h-4 w-4" />

            Crear suscripción
          </Button>
        </div>

        {error ? (
          <Alert variant="destructive">
            <AlertTitle>
              Error
            </AlertTitle>

            <AlertDescription>
              {error}
            </AlertDescription>
          </Alert>
        ) : null}

        {selectedItems.length >
        0 ? (
          <div className="space-y-3">
            <div className="text-sm font-medium">
              Planes que dan acceso
            </div>

            <div className="flex flex-wrap gap-2">
              {selectedItems.map(
                (item) => (
                  <button
                    key={
                      item.recurring_id
                    }
                    type="button"
                    onClick={() =>
                      toggleSubscription(
                        item.recurring_id
                      )
                    }
                    className="inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition hover:bg-muted"
                  >
                    <span>
                      {item.name}
                    </span>

                    <span className="text-muted-foreground">
                      {formatMoney(
                        item.price
                      )}
                    </span>
                  </button>
                )
              )}
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-dashed p-4 text-sm text-muted-foreground">
            No hay suscripciones
            seleccionadas. El acceso por
            suscripción está desactivado.
          </div>
        )}

        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />

          <Input
            value={query}
            onChange={(e) =>
              setQuery(
                e.target.value
              )
            }
            placeholder="Buscar y seleccionar suscripción..."
            className="pl-9"
          />
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />

            Cargando suscripciones...
          </div>
        ) : filtered.length ===
          0 ? (
          <div className="rounded-xl border border-dashed p-6 text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
              <CreditCard className="h-5 w-5 text-muted-foreground" />
            </div>

            <h3 className="font-medium">
              No hay suscripciones
              para mostrar
            </h3>

            <p className="mt-1 text-sm text-muted-foreground">
              Crea un plan o usa el
              buscador con otro término.
            </p>

            <div className="mt-4">
              <Button
                type="button"
                onClick={() =>
                  setOpenCreate(
                    true
                  )
                }
                className="gap-2"
              >
                <Plus className="h-4 w-4" />

                Crear suscripción
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(
              (item) => {
                const id =
                  Number(
                    item.recurring_id
                  )

                const selected =
                  selectedIds.includes(
                    id
                  )

                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() =>
                      toggleSubscription(
                        id
                      )
                    }
                    className={cn(
                      'flex w-full items-start justify-between gap-4 rounded-xl border p-4 text-left transition',

                      selected
                        ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                        : 'hover:bg-muted/40'
                    )}
                  >
                    <div className="space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="font-medium">
                          {
                            item.name
                          }
                        </div>

                        <Badge
                          variant={
                            item.status
                              ? 'default'
                              : 'secondary'
                          }
                        >
                          {item.status
                            ? 'Activo'
                            : 'Inactivo'}
                        </Badge>

                        {item.trial_status ? (
                          <Badge variant="secondary">
                            Trial
                          </Badge>
                        ) : null}

                        {selected ? (
                          <Badge>
                            Seleccionado
                          </Badge>
                        ) : null}
                      </div>

                      <div className="text-sm text-muted-foreground">
                        {formatMoney(
                          item.price
                        )}{' '}
                        cada{' '}
                        {item.cycle}{' '}
                        {frequencyLabel(
                          item.frequency
                        )}

                        {item.duration >
                        0
                          ? ` · duración ${item.duration}`
                          : ' · recurrente'}
                      </div>

                      {item.trial_status ? (
                        <div className="text-xs text-muted-foreground">
                          Trial:{' '}
                          {formatMoney(
                            item.trial_price
                          )}{' '}
                          cada{' '}
                          {
                            item.trial_cycle
                          }{' '}
                          {frequencyLabel(
                            item.trial_frequency
                          )}
                        </div>
                      ) : null}
                    </div>

                    <div
                      className={cn(
                        'mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border',

                        selected
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-muted-foreground/30'
                      )}
                    >
                      {selected ? (
                        <Check className="h-4 w-4" />
                      ) : null}
                    </div>
                  </button>
                )
              }
            )}
          </div>
        )}
      </Card>

      <CreateSubscriptionModal
        open={openCreate}
        onOpenChange={
          setOpenCreate
        }
        onCreated={(
          subscription
        ) => {
          setItems((prev) => [
            subscription,
            ...prev,
          ])

          const id =
            Number(
              subscription.recurring_id
            )

          if (
            id &&
            !selectedIds.includes(
              id
            )
          ) {
            onChange([
              ...selectedIds,
              id,
            ])
          }
        }}
      />
    </>
  )
}